import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { useCreateDecision } from "@/application/use-cases/decisions/use-decisions.queries";
import { useCurrentPhase } from "@/application/use-cases/phases/use-phases.queries";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { WeekSelect } from "@/presentation/components/weeks/week-select";
import { Button } from "@monorepo-apps/ui/src/components/button";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

export const Route = createFileRoute("/_app/decisions/new")({
  component: NewDecisionPage,
});

function NewDecisionPage() {
  const navigate = useNavigate();
  const create = useCreateDecision();
  const currentPhase = useCurrentPhase();
  const [title, setTitle] = useState("");
  const [rationale, setRationale] = useState("");
  const [alternatives, setAlternatives] = useState("");
  const [gate, setGate] = useState("");
  const [phaseId, setPhaseId] = useState<string | null>(null);
  const [phaseTouched, setPhaseTouched] = useState(false);

  const effectivePhaseId = phaseTouched ? phaseId : (currentPhase.data?.id ?? null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const created = await create.mutateAsync({
        title,
        rationale: rationale || null,
        alternatives: alternatives || null,
        gate: gate || null,
        owner_id: null,
        status: "open",
        decided_at: null,
        phase_id: effectivePhaseId,
      });
      toast.success("Decision created");
      void navigate({ to: "/decisions", search: { id: created.id } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create");
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader title="New decision" />
      <form onSubmit={onSubmit} className="flex max-w-2xl flex-col gap-4 px-6 py-6">
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
        </Field>
        <Field label="Alternatives considered">
          <Textarea
            value={alternatives}
            onChange={(e) => setAlternatives(e.target.value)}
            rows={2}
          />
        </Field>
        <Field label="Rationale">
          <Textarea value={rationale} onChange={(e) => setRationale(e.target.value)} rows={4} />
        </Field>
        <Field label="Gate (e.g. phase-1, phase-1-wms)">
          <Input value={gate} onChange={(e) => setGate(e.target.value)} />
        </Field>
        <Field label="Week">
          <WeekSelect
            value={effectivePhaseId}
            onChange={(next) => {
              setPhaseTouched(true);
              setPhaseId(next);
            }}
            ariaLabel="Decision week"
          />
        </Field>
        <div className="flex gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => void navigate({ to: "/decisions" })}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-muted-foreground text-[10px] tracking-wider uppercase">{label}</Label>
      {children}
    </div>
  );
}
